import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useStore } from '../context/StoreContext';
import { useToast } from '../hooks/useToast';
import { useScrollReveal } from '../hooks/useParallax';
import ToastContainer from '../components/Toast';
import api from '../utils/api';
import { normalizeProduct } from '../utils/productUtils';
import localProducts from '../data/products.json';

export default function TrendingPage() {
  const { addToCart } = useStore();
  const { toasts, showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useScrollReveal([loading]);

  useEffect(() => {
    let ignore = false;

    const loadTrendingProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get('/products', {
          params: { trending: true },
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!ignore) {
          const raw = Array.isArray(response.data)
            ? response.data
            : response.data.products || response.data.data || [];
          const normalized = raw.map(normalizeProduct);
          setProducts(normalized);
        }
      } catch (fetchError) {
        console.error(fetchError);
        if (!ignore) {
          setProducts(localProducts.map(normalizeProduct));
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadTrendingProducts();

    return () => {
      ignore = true;
    };
  }, []);

  const trending = products.filter((product) => product.trending);
  const handleAddToCart = p => {
    addToCart({ ...p, variant: `${p.colors[0]} / ${p.sizes[0]}`, qty: 1 });
    showToast(`${p.name} added to cart`, 'fa-bag-shopping');
  };

  return (
    <div className="trending-page">
      {/* Hero */}
      <div className="trending-hero">
        {/* Animated rings */}
        <div className="rings-container">
          {[180, 300, 440].map((size, i) => (
            <div 
              key={i} 
              className="ring-item"
              style={{
                width: size, 
                height: size, 
                animation: `ringPulse ${2.2 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`,
              }} 
            />
          ))}
        </div>

        <div className="container text-center">
          <div className="trending-badge">
            <i className="fas fa-fire" />
            <span>What's Hot Now</span>
          </div>

          <h1 className="trending-title">
            Trending <em>This Season</em>
          </h1>
          <p className="trending-desc">
            {trending.length} hand-picked bestsellers our customers absolutely love
          </p>

          <div className="stats-container">
            {[
              { num: '2,400+', label: 'Happy Customers' },
              { num: trending.length, label: 'Trending Now' },
              { num: '4.8★', label: 'Average Rating' },
            ].map(s => (
              <div key={s.label} className="stat-item">
                <div className="stat-num">{s.num}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Products */}
      <section className="section-pad products-section">
        <div className="container">
          <div className="row g-4">
            {loading ? (
              <div className="col-12">
                <p className="text-center mb-0">Loading trending products...</p>
              </div>
            ) : error ? (
              <div className="col-12">
                <p className="text-center mb-0">{error}</p>
              </div>
            ) : trending.length > 0 ? (
              trending.map((p, i) => (
                <div key={p.id} className="col-6 col-md-4 col-lg-3 reveal" style={{ transitionDelay: `${i * 0.07}s` }}>
                  <ProductCard product={p} onAddToCart={handleAddToCart} />
                </div>
              ))
            ) : (
              <div className="col-12">
                <p className="text-center mb-0">No trending products are available right now.</p>
              </div>
            )}
          </div>
          <div className="text-center mt-5 reveal">
            <Link to="/shop" className="btn-gold btn-browse">
              <i className="fas fa-store" /> Browse All Products
            </Link>
          </div>
        </div>
      </section>

      <ToastContainer toasts={toasts} />
    </div>
  );
}